import { isNativePlatform, getPlatform } from '../native-features';

interface LocalNotificationOptions {
  id: number;
  title: string;
  body: string;
  data?: Record<string, string>;
}

const DEFAULT_CHANNEL_ID = 'opexmes-notifications';

let channelCreated = false;

/**
 * Ensure the Android notification channel exists (required on Android 8+).
 */
async function ensureChannel(): Promise<void> {
  if (channelCreated || getPlatform() !== 'android') return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.createChannel({
      id: DEFAULT_CHANNEL_ID,
      name: 'OpexMES Notifications',
      description: 'General notifications from OpexMES',
      importance: 5,
      visibility: 1,
      lights: true,
      vibration: true,
    });
    channelCreated = true;
  } catch (e) {
    console.warn('[LocalNotif] Failed to create channel:', e);
  }
}

/**
 * Show a local notification on the device.
 * Native: uses @capacitor/local-notifications.
 * Web: falls back to browser Notification API.
 */
export async function showLocalNotification(options: LocalNotificationOptions): Promise<void> {
  if (isNativePlatform()) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      await ensureChannel();
      await LocalNotifications.schedule({
        notifications: [
          {
            title: options.title,
            body: options.body,
            id: options.id,
            extra: options.data,
            channelId: DEFAULT_CHANNEL_ID,
            ...(getPlatform() === 'android'
              ? { smallIcon: 'ic_notification' }
              : {}),
          },
        ],
      });
    } catch (e) {
      console.warn('[LocalNotif] Failed to schedule:', e);
    }
  } else {
    // Web fallback: browser Notification API
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(options.title, {
        body: options.body,
        data: options.data,
      });
    }
  }
}

/**
 * Request notification permission on the current platform.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (isNativePlatform()) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      await ensureChannel();
      const result = await LocalNotifications.requestPermissions();
      return result.display === 'granted';
    } catch {
      return false;
    }
  } else {
    if ('Notification' in window) {
      const result = await Notification.requestPermission();
      return result === 'granted';
    }
    return false;
  }
}
